import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, Instagram, Mail, Phone, Check } from "lucide-react";
import { FadeIn } from "../components/FadeIn";
import { MagneticButton } from "../components/MagneticButton";
import { toast } from "sonner";
import { useT, usePageMeta } from "../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPE_KEYS = [
  "contact.type.expedition",
  "contact.type.doc",
  "contact.type.sport",
  "contact.type.edito",
  "contact.type.print",
  "contact.type.other",
];

export default function Contact() {
  const t = useT();
  usePageMeta({ titleKey: "meta.contact.title", descKey: "meta.contact.desc", path: "/contact" });
  // State holds canonical i18n keys so the payload stays language-agnostic
  const [form, setForm] = useState({ name: "", email: "", project_type: PROJECT_TYPE_KEYS[0], message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Send both the translated label (for the email body) and the canonical key.
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        message: form.message,
        project_type: t(form.project_type),
        project_type_key: form.project_type,
      });
      setSent(true);
      toast.success(t("contact.toast.success"));
      setForm({ name: "", email: "", project_type: PROJECT_TYPE_KEYS[0], message: "" });
    } catch (err) {
      console.error(err);
      toast.error(t("contact.toast.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-bone text-ink min-h-screen pt-32">
      <section className="container-x pb-32">
        <FadeIn>
          <div className="eyebrow mb-4" style={{ color: "#4A4E57" }}>{t("contact.eyebrow")}</div>
          <h1 className="font-display text-6xl md:text-9xl leading-[0.9] mb-10">
            {t("contact.pageHeroA")} <span className="italic text-stone">{t("contact.pageHeroIt")}</span>.
          </h1>
        </FadeIn>

        <div className="grid md:grid-cols-5 gap-16 mt-16">
          <div className="md:col-span-2 space-y-10">
            <FadeIn>
              <p className="text-ink/70 max-w-md leading-relaxed">
                {t("contact.intro2")}
              </p>
            </FadeIn>
            <FadeIn delay={0.1}>
              <div className="border-t border-ink/20 pt-6 space-y-5">
                <div>
                  <div className="eyebrow mb-2" style={{ color: "#4A4E57" }}>{t("contact.label.email")}</div>
                  <a href="mailto:romain.bruneauprod@gmail.com" data-testid="contact-email" className="text-lg hover:text-stone transition-colors flex items-center gap-2">
                    <Mail size={16} /> romain.bruneauprod@gmail.com
                  </a>
                </div>
                <div>
                  <div className="eyebrow mb-2" style={{ color: "#4A4E57" }}>{t("contact.label.phone")}</div>
                  <a href="tel:+33606465046" data-testid="contact-phone" className="text-lg hover:text-stone transition-colors flex items-center gap-2">
                    <Phone size={16} /> +33 6 06 46 50 46
                  </a>
                </div>
                <div>
                  <div className="eyebrow mb-2" style={{ color: "#4A4E57" }}>{t("contact.label.socials")}</div>
                  <div className="space-y-2">
                    <a href="https://www.instagram.com/romain.bruneau/" target="_blank" rel="noreferrer" data-testid="contact-instagram" className="hover:text-stone flex items-center gap-2">
                      <Instagram size={16} /> @romain.bruneau
                    </a>
                  </div>
                </div>
                <div>
                  <div className="eyebrow mb-2" style={{ color: "#4A4E57" }}>{t("contact.label.base")}</div>
                  <div className="text-ink/80">{t("contact.baseValue")}</div>
                </div>
                <div data-testid="contact-legal" className="pt-4 border-t border-ink/15">
                  <div className="eyebrow mb-2" style={{ color: "#4A4E57" }}>{t("contact.label.legal")}</div>
                  <div className="text-sm text-ink/70 leading-relaxed">
                    <div className="font-medium text-ink/90">NOVEA PRODUCTION</div>
                    <div>10 Avenue du Cheminet</div>
                    <div>44120 Vertou · France</div>
                    <div className="mt-2 text-ink/60">
                      SIRET 979 815 750 00042<br />
                      TVA FR02 979 815 750
                    </div>
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>

          <div className="md:col-span-3">
            {sent ? (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="border border-ink/20 p-12 text-center">
                <div className="w-16 h-16 border border-stone rounded-full mx-auto mb-6 flex items-center justify-center text-stone">
                  <Check size={26} />
                </div>
                <h3 className="font-display text-3xl mb-4">{t("contact.form.sent.title")}</h3>
                <p className="text-ink/70">{t("contact.form.sent.sub")}</p>
                <button
                  data-testid="contact-new"
                  onClick={() => setSent(false)}
                  className="mt-8 text-stone text-[11px] tracking-[0.22em] uppercase hover:text-ink"
                >
                  {t("contact.form.sent.again")}
                </button>
              </motion.div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit} className="space-y-8">
                <div>
                  <div className="field-label light mb-2">{t("contact.form.name")}</div>
                  <input data-testid="contact-name" required value={form.name} onChange={onChange("name")} className="field light" placeholder={t("contact.form.namePh")} />
                </div>
                <div>
                  <div className="field-label light mb-2">{t("contact.email")}</div>
                  <input data-testid="contact-email-input" type="email" required value={form.email} onChange={onChange("email")} className="field light" placeholder={t("contact.form.emailPh")} />
                </div>
                <div>
                  <div className="field-label light mb-2">{t("contact.form.type")}</div>
                  <select data-testid="contact-type" value={form.project_type} onChange={onChange("project_type")} className="field light bg-bone">
                    {PROJECT_TYPE_KEYS.map((k) => <option key={k} value={k}>{t(k)}</option>)}
                  </select>
                </div>
                <div>
                  <div className="field-label light mb-2">{t("contact.form.message")}</div>
                  <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={onChange("message")} className="field light resize-none" placeholder={t("contact.form.messagePh")} />
                </div>
                <MagneticButton
                  type="submit"
                  data-testid="contact-submit"
                  className="btn-line dark mt-4"
                  disabled={loading}
                >
                  <span>{loading ? t("contact.form.submitting") : t("contact.form.submit")}</span>
                  <Send size={14} />
                </MagneticButton>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
